import {
  BookOutlined,
  CloseCircleOutlined,
  DownOutlined,
  InfoOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Badge, Button, Col, Dropdown, Modal, Row, Table, Tooltip } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import EditIcon from 'assets/icons/edit-icon.svg?react';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { customFleets } from 'shared';
import styled from 'styled-components';

import { ShopStore, WorkOrderStore } from 'stores';
import { EventBus } from 'stores/EventBus';

const StyledButton = styled(Button)`
  &.mark-resolved {
    margin-left: 5px;
  }
`;

const DefectResolveButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .ant-btn {
    &.pitstopDropdown {
      color: #333;
      font-weight: bold;
      font-size: 1.2rem;
      padding-bottom: 30px;
      padding: 0.3rem 1.5rem;
      border-radius: 0.3rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #d9d9d9;
      background-color: white;

      &.green {
        border-color: green;
        color: green;
      }

      &.red {
        border-color: red;
        color: red;
      }

      &.disabled {
        cursor: not-allowed;
        background-color: #f5f5f5;
      }
    }

    &.save-btn {
      margin-top: 10px;
    }
  }
`;

const MechanicNotesWrapper = styled.div`
  position: relative;

  svg {
    width: 16px;
  }

  .mechanic-notes-edit-actions {
    position: absolute;
    right: 10px;
    bottom: 0;

    &.editing-note {
      bottom: 10px;
    }

    svg {
      cursor: pointer;
    }
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 75px;
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
`;

const ResponsiveTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  .ant-table {
    min-width: 800px;
  }

  @media (max-width: 768px) {
    .ant-table-thead > tr > th {
      padding: 8px;
      font-size: 0.875rem;
    }

    .ant-table-tbody > tr > td {
      padding: 8px;
      font-size: 0.875rem;
    }

    .ant-btn {
      font-size: 0.875rem;
      padding: 4px 8px;
    }
  }
`;

const DefectTypeHeader = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseIcon = styled(CloseCircleOutlined)`
  position: absolute;
  top: -10px;
  right: -10px;
  font-size: 20px;
  padding: 5px;
  color: white;
  cursor: pointer;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.7);
`;
const VehicleDefectsTable = ({
  data,
  loading,
  onDefectResolveClick,
  onDefectMechanicNotesChange,
  onSaveMechanicNotes,
  onConfirmResolveDefect,
  onMarkAllAsResolvedClick,
  onCreateWorkOrderFromDefectsClick,
  onCreateTMTWorkOrderFromDefectsClick,
  createTmtWorkOrdersEnabled,
  createWorkOrderFromDefectsEnabled,
  isGeotabFleet,
  childRef,
  fetchError,
}) => {
  const [selectedDefects, setSelectedDefects] = useState([]);

  const [editingMechanicNotes, setEditingMechanicNotes] = useState([]);

  const [resolvingDefects, setResolvingDefects] = useState([]);

  const [viewPhotos, setViewPhotos] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const handlePrev = () => {
    setCurrentPhotoIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : photos.length - 1
    );
  };

  const handleNext = () => {
    setCurrentPhotoIndex((nextIndex) =>
      nextIndex < photos.length - 1 ? nextIndex + 1 : 0
    );
  };

  // just a way to expose the editingMechanicNotes state to the parent component
  childRef(() => {
    return {
      editingMechanicNotes,
      setEditingMechanicNotes,
      resolvingDefects,
      setResolvingDefects,
    };
  });

  const baseColumns = [
    {
      dataIndex: 'defectPhotos',
      key: 'defectPhotos',
      align: 'center',
      render: (defectPhotos) => {
        if (!defectPhotos || !defectPhotos.length) {
          return null;
        }
        return (
          <Badge
            count={defectPhotos.length}
            style={{ backgroundColor: '#292929' }}
          >
            <ImageWrapper
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                setViewPhotos(true);
                setPhotos(defectPhotos);
                setCurrentPhotoIndex(0);
              }}
            >
              <img
                src={defectPhotos[0].url}
                style={{
                  width: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            </ImageWrapper>
          </Badge>
        );
      },
    },
    {
      title: 'Defect',
      dataIndex: 'defectType',
      key: 'defectType',
      render: (defectType, row) => {
        return (
          <div>
            <DefectTypeHeader>
              {defectType}
              {row.geotabStatus === 'NotNecessary' && (
                <Tooltip
                  title="The issue detected is not critical to the safe operation of your vehicle. No immediate action is required to ensure your vehicle remains safe to drive."
                  placement="top"
                >
                  <InfoCircleOutlined
                    style={{
                      color: '#1890ff',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  />
                </Tooltip>
              )}
            </DefectTypeHeader>
            <div>
              {row.workOrder ? (
                <>
                  <a
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const workOrderData =
                          await WorkOrderStore.getWorkOrderById(
                            row.workOrder.work_order_id
                          );
                        EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
                          type: 'work-order',
                          data: workOrderData,
                          title: `Work Order #${workOrderData.invoice_number || workOrderData.id}`,
                        });
                      } catch (error) {
                        console.error('Error fetching work order:', error);
                        window.location.href = `/work-order/${row.workOrder.work_order_id}/edit`;
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <BookOutlined />
                    <span className="marginLeft5">Work Order</span>
                  </a>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Added On',
      dataIndex: 'createdAtTime',
      key: 'createdAtTime',
      sorter: (a, b) => {
        return moment(a.createdAtTime).unix() - moment(b.createdAtTime).unix();
      },
      render: (createdAtTime) => {
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {moment(createdAtTime).format('MMMM Do YYYY')}
            </div>
            <div>{moment(createdAtTime).format('LT')}</div>
          </div>
        );
      },
    },
    {
      title: <>{isGeotabFleet ? 'Remarks' : 'Driver Notes'}</>,
      dataIndex: 'comment',
      key: 'comment',
      render: (comment) => {
        return comment || 'N/A';
      },
    },
  ];

  baseColumns.push({
    title: <>{isGeotabFleet ? 'New Remark' : 'Mechanic Notes'}</>,
    dataIndex: 'mechanicNotes',
    key: 'mechanicNotes',
    align: 'center',
    width: '15%',
    render: (mechanicNotes, row) => {
      return (
        <MechanicNotesWrapper>
          <TextArea
            rows={4}
            id={`mechanic-notes-${row.id}`}
            value={mechanicNotes}
            onChange={(e) => {
              const value = e.target.value;
              onDefectMechanicNotesChange(row, value);
              setEditingMechanicNotes([...editingMechanicNotes, row.id]);
            }}
          />
          <div
            className={
              'mechanic-notes-edit-actions ' +
              (editingMechanicNotes.indexOf(row.id) > -1 ? 'editing-note' : '')
            }
          >
            {editingMechanicNotes.indexOf(row.id) > -1 ? (
              <>
                <Button
                  type="primary"
                  size="small"
                  ghost
                  onClick={() => {
                    setEditingMechanicNotes(
                      editingMechanicNotes.filter((id) => id !== row.id)
                    );
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="small"
                  style={{ marginLeft: 5 }}
                  onClick={() => {
                    onSaveMechanicNotes(row, row.mechanicNotes);
                  }}
                >
                  Save
                </Button>
              </>
            ) : (
              <EditIcon
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingMechanicNotes([...editingMechanicNotes, row.id]);
                  // focus on text area
                  document.getElementById(`mechanic-notes-${row.id}`).focus();
                }}
              />
            )}
          </div>
        </MechanicNotesWrapper>
      );
    },
  });

  baseColumns.push({
    title: '',
    dataIndex: 'actions',
    key: 'actions',
    render: (unused, row) => {
      return (
        !fetchError && (
          <DefectResolveButtonWrapper>
            <Dropdown
              className="pitstopDropdown"
              disabled={row.isResolved}
              menu={{
                items: [
                  {
                    key: 'resolved',
                    label: 'Resolved',
                  },
                  {
                    key: 'unresolved',
                    label: 'Unresolved',
                  },
                ],
                onClick: ({ key }) => {
                  onDefectResolveClick(key, row);
                  if (key === 'resolved') {
                    setResolvingDefects([...resolvingDefects, row.id]);
                  } else {
                    setResolvingDefects(
                      resolvingDefects.filter((id) => id !== row.id)
                    );
                  }
                },
              }}
              placement="bottomCenter"
            >
              <Button
                className={`${row.isResolved ? 'green' : 'red'} ${row.resolvedBy ? 'disabled' : ''}`}
                disabled={row.isResolved}
              >
                {row.isResolved ? 'Resolved' : 'Unresolved'}
                {!row.isResolved && <DownOutlined />}
              </Button>
            </Dropdown>
            {resolvingDefects.indexOf(row.id) > -1 && (
              <Button
                className="save-btn"
                type="primary"
                onClick={() => {
                  onConfirmResolveDefect(row);
                }}
              >
                Confirm Resolution
              </Button>
            )}
          </DefectResolveButtonWrapper>
        )
      );
    },
  });
  // }

  const columns = baseColumns;

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedDefects(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: !!record.resolvedBy,
      name: record.name,
    }),
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Row gutter={[16, 16]}>
            {!fetchError && (
              <>
                <StyledButton
                  disabled={selectedDefects.length === 0}
                  type="primary"
                  className="mark-resolved"
                  onClick={() => {
                    onMarkAllAsResolvedClick(selectedDefects);
                  }}
                >
                  Mark all as resolved{' '}
                  {selectedDefects.length > 0 && `(${selectedDefects.length})`}
                </StyledButton>
                <Tooltip
                  placement="right"
                  title={
                    'Resolve multiple defects at once. If all defects are resolved, the DVIR will also be resolved'
                  }
                >
                  <Button
                    style={{ marginLeft: 10, marginRight: 20 }}
                    size="small"
                    shape="circle"
                    icon={<InfoOutlined />}
                  />
                </Tooltip>
              </>
            )}
            {customFleets.summitMaterialsAll.includes(
              ShopStore.currentShop.id
            ) ? (
              <>
                <StyledButton
                  disabled={selectedDefects.length === 0}
                  type="primary"
                  ghost
                  className="create-workorder"
                  onClick={() => {
                    onCreateTMTWorkOrderFromDefectsClick(selectedDefects);
                  }}
                >
                  Create TMT Work Order From Defects{' '}
                  {selectedDefects.length > 0 && `(${selectedDefects.length})`}
                </StyledButton>
                <Tooltip
                  placement="right"
                  title={
                    'Create Work Order From Defects. Select the defects you want create a Work Order'
                  }
                >
                  <Button
                    style={{ marginLeft: 10, marginRight: 20 }}
                    size="small"
                    shape="circle"
                    icon={<InfoOutlined />}
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <StyledButton
                  disabled={selectedDefects.length === 0}
                  type="primary"
                  ghost
                  className="create-workorder"
                  onClick={() => {
                    onCreateWorkOrderFromDefectsClick(selectedDefects);
                  }}
                >
                  Create Work Order From Defects{' '}
                  {selectedDefects.length > 0 && `(${selectedDefects.length})`}
                </StyledButton>
                <Tooltip
                  placement="right"
                  title={
                    'Create Work Order From Defects. Select the defects you want create a Work Order'
                  }
                >
                  <Button
                    style={{ marginLeft: 10, marginRight: 20 }}
                    size="small"
                    shape="circle"
                    icon={<InfoOutlined />}
                  />
                </Tooltip>
              </>
            )}
          </Row>
        </Col>
        <Col span={24}>
          <ResponsiveTableWrapper>
            <Table
              rowKey={(record) => record.id}
              rowSelection={rowSelection}
              dataSource={data}
              columns={columns}
              loading={loading}
            />
          </ResponsiveTableWrapper>
        </Col>
      </Row>
      <Modal
        visible={viewPhotos}
        footer={null}
        closeIcon={<CloseIcon />}
        onCancel={() => {
          setViewPhotos(false);
          setCurrentPhotoIndex(0);
          setPhotos([]);
        }}
        onOk={() => {
          setViewPhotos(false);
          setCurrentPhotoIndex(0);
          setPhotos([]);
        }}
      >
        {photos.length > 0 && (
          <>
            <img
              src={photos[currentPhotoIndex].url}
              alt={`Photo ${currentPhotoIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '600px',
                objectFit: 'contain',
                display: 'block',
                margin: 'auto',
              }}
            />
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <span>
                Page {currentPhotoIndex + 1} of {photos.length}
              </span>
            </div>
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <Button
                onClick={handlePrev}
                style={{ marginRight: '8px' }}
                disabled={currentPhotoIndex === 0}
                type="primary"
              >
                Previous
              </Button>
              <Button
                onClick={handleNext}
                disabled={currentPhotoIndex === photos.length - 1}
                type="primary"
              >
                Next
              </Button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

VehicleDefectsTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  onDefectResolveClick: PropTypes.func.isRequired,
  onDefectMechanicNotesChange: PropTypes.func.isRequired,
  onSaveMechanicNotes: PropTypes.func.isRequired,
  onConfirmResolveDefect: PropTypes.func.isRequired,
  onMarkAllAsResolvedClick: PropTypes.func.isRequired,
  onCreateWorkOrderFromDefectsClick: PropTypes.func,
  onCreateTMTWorkOrderFromDefectsClick: PropTypes.func,
  createTmtWorkOrdersEnabled: PropTypes.bool,
  createWorkOrderFromDefectsEnabled: PropTypes.bool,
  isGeotabFleet: PropTypes.bool,
  childRef: PropTypes.func,
  fetchError: PropTypes.object,
};

VehicleDefectsTable.defaultProps = {
  data: [],
  loading: false,
  childRef: () => {},
  onCreateWorkOrderFromDefectsClick: () => {},
  onCreateTMTWorkOrderFromDefectsClick: () => {},
  createTmtWorkOrdersEnabled: true,
  createWorkOrderFromDefectsEnabled: true,
  isGeotabFleet: false,
};

export default VehicleDefectsTable;
